import React, { useEffect, useMemo, useState } from "react";
import { Button, Result, Spin, message } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { useCompany } from "@redux";
import { serverFetch } from "@extensions";
import CatalogSale from "../catalog/catalog-sale";
import Payment from "../payment/payment";
import "./ad-sale.css";

const getPathFromUrl = (url) => {
  if (!url) return "";

  try {
    return new URL(url, window.location.origin).pathname.replace(/\/+$/, "");
  } catch {
    return String(url).split("?")[0].replace(/\/+$/, "");
  }
};

const normalizePath = (path) => (path || "").replace(/\/+$/, "") || "/";

const getItemUrlValues = (item) => [
  item?.url,
  item?.adUrl,
  item?.link,
  item?.saleUrl,
  item?.slug,
  item?.code,
  item?.alias,
].filter(Boolean);

const AdSale = () => {
  const company = useCompany();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentSales, setPaymentSales] = useState([]);

  const currentPath = normalizePath(location.pathname);
  const companyAdPath = normalizePath(getPathFromUrl(company?.adUrl));
  const adUrlMatchesPage = company?.adUrl && companyAdPath === currentPath;

  const adItem = useMemo(() => {
    if (!items?.length) return null;

    const pathParts = currentPath.split("/").filter(Boolean);
    const lastPathPart = pathParts[pathParts.length - 1];
    const numericId = Number(lastPathPart);

    return items.find((item) => {
      if (Number.isFinite(numericId) && (item.id === numericId || item.productId === numericId || item.catalogId === numericId)) {
        return true;
      }

      return getItemUrlValues(item).some((value) => {
        const normalizedValue = normalizePath(getPathFromUrl(value));
        return normalizedValue === currentPath || normalizedValue === companyAdPath || normalizePath(String(value)) === normalizePath(lastPathPart);
      });
    }) || null;
  }, [companyAdPath, currentPath, items]);

  useEffect(() => {
    if (!company || !adUrlMatchesPage) return;

    let cleanup = false;
    setLoading(true);

    serverFetch("Online/Products", { method: "GET" })
      .then((data) => {
        if (!cleanup) {
          setItems(data || []);
        }
      })
      .catch((error) => {
        message.error(error?.UserMessage || "Не удалось загрузить рекламную услугу");
      })
      .finally(() => {
        if (!cleanup) {
          setLoading(false);
        }
      });

    return () => {
      cleanup = true;
    };
  }, [adUrlMatchesPage, company]);

  if (!company?.adUrl || !adUrlMatchesPage) {
    return (
      <div className="ad-sale-page">
        <Result
          status="404"
          title="Страница не найдена"
          extra={<Button onClick={() => navigate("/home")}>На главную</Button>}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ad-sale-page ad-sale-page_loading">
        <Spin size="large" />
      </div>
    );
  }

  if (!adItem) {
    return (
      <div className="ad-sale-page">
        <Result
          status="warning"
          title="Рекламная услуга не найдена"
          subTitle="Проверьте URL услуги в настройках компании."
          extra={<Button onClick={() => navigate("/home")}>На главную</Button>}
        />
      </div>
    );
  }

  return (
    <div className="ad-sale-page">
      <CatalogSale
        item={{ ...adItem, catalogId: adItem.catalogId || adItem.id }}
        open={true}
        mode="page"
        onClose={() => navigate("/home")}
        onPurchaseSuccess={() => navigate("/sales")}
        onPaymentOpen={(sales) => {
          setPaymentSales(sales);
          setPaymentOpen(true);
        }}
      />
      <Payment sales={paymentSales} open={paymentOpen} onClose={() => setPaymentOpen(false)} />
    </div>
  );
};

export default AdSale;
